'use client';

import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { XIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';

const BlurOverlayForegroundContext = createContext<((hasForeground: boolean) => void) | null>(null);

const FOREGROUND_SELECTOR = '[aria-modal="true"], [role="dialog"]';

function overlayHasForegroundContent(overlay: HTMLElement | null): boolean {
  return !!overlay?.querySelector(FOREGROUND_SELECTOR);
}

/** Children call this so the parent blur shell can hide its fallback close control. */
export function useBlurOverlayForegroundPresence(hasForeground: boolean) {
  const register = useContext(BlurOverlayForegroundContext);
  useLayoutEffect(() => {
    register?.(hasForeground);
    return () => register?.(false);
  }, [hasForeground, register]);
}

export type DismissibleBlurOverlayProps = {
  onClose: () => void;
  children?: React.ReactNode;
  closeLabel?: string;
  /** `absolute` for in-tree shells; `fixed` for portaled backdrops. */
  position?: 'absolute' | 'fixed';
  className?: string;
  zIndexClass?: string;
  /** When true, Escape is ignored while any `[aria-modal="true"]` element exists. */
  deferEscapeToNestedModals?: boolean;
  /**
   * Show the upper-right dismiss control. When omitted, the shell shows it only
   * while no child has registered foreground content via ``useBlurOverlayForegroundPresence``.
   */
  showCloseButton?: boolean;
  /** Close when the user clicks the backdrop (not the close button or children). */
  dismissOnBackdropClick?: boolean;
};

export function BlurOverlayCloseButton({
  onClose,
  closeLabel = 'Close overlay',
  className = '',
}: {
  onClose: () => void;
  closeLabel?: string;
  className?: string;
}) {
  return (
    <Button
      type="button"
      iconOnly
      size="sm"
      style="text"
      color="light"
      background="inverse"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      aria-label={closeLabel}
      className={`fixed top-[68px] right-4 z-[1] flex cursor-pointer items-center justify-center rounded-full bg-gray-800/90 p-2 text-white shadow-lg ring-1 ring-white/20 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-white/40 sm:top-[80px] ${className}`.trim()}
    >
      <XIcon weight="bold" />
    </Button>
  );
}

/**
 * Full-viewport blur shell with guaranteed dismiss paths (Escape + optional close control).
 * Use anywhere a backdrop-blur overlay can outlive its foreground content.
 */
export function DismissibleBlurOverlay({
  onClose,
  children,
  closeLabel = 'Close overlay',
  position = 'absolute',
  className = '',
  zIndexClass = 'z-50',
  deferEscapeToNestedModals = true,
  showCloseButton,
  dismissOnBackdropClick = false,
}: DismissibleBlurOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [childReportsForeground, setChildReportsForeground] = useState(false);
  const [domHasForeground, setDomHasForeground] = useState(false);
  const registerForegroundPresence = useCallback((hasForeground: boolean) => {
    setChildReportsForeground(hasForeground);
  }, []);

  const childHasForeground = childReportsForeground || domHasForeground;

  const effectiveShowCloseButton =
    showCloseButton !== undefined
      ? showCloseButton
      : children
        ? !childHasForeground
        : true;

  useLayoutEffect(() => {
    if (showCloseButton !== undefined || !children) return;

    const overlay = overlayRef.current;
    if (!overlay) return;

    const syncDomForeground = () => {
      setDomHasForeground(overlayHasForegroundContent(overlay));
    };

    syncDomForeground();

    const observer = new MutationObserver(syncDomForeground);
    observer.observe(overlay, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal', 'role'],
    });

    return () => observer.disconnect();
  }, [showCloseButton, children]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (deferEscapeToNestedModals && document.querySelector('[aria-modal="true"]')) {
        return;
      }
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, deferEscapeToNestedModals]);

  const positionClass = position === 'fixed' ? 'fixed inset-0' : 'absolute top-0 left-0 h-screen w-screen';

  return (
    <div
      ref={overlayRef}
      className={`${positionClass} ${zIndexClass} backdrop-blur-sm ${className}`.trim()}
      data-blur-overlay-dismissible
      data-evidence-preview-dismiss-overlay={dismissOnBackdropClick ? true : undefined}
      aria-hidden={dismissOnBackdropClick ? true : undefined}
      onMouseDown={
        dismissOnBackdropClick
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              e.preventDefault();
              e.stopPropagation();
            }
          : undefined
      }
      onClick={
        dismissOnBackdropClick
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              e.stopPropagation();
              onClose();
            }
          : undefined
      }
    >
      {effectiveShowCloseButton && (
        <BlurOverlayCloseButton onClose={onClose} closeLabel={closeLabel} />
      )}
      <BlurOverlayForegroundContext.Provider value={registerForegroundPresence}>
        {children}
      </BlurOverlayForegroundContext.Provider>
    </div>
  );
}
